"use client";

import { ArrowUpRight } from "lucide-react";
import { siFacebook, siInstagram, siTelegram, siWhatsapp, siX, siYoutube } from "simple-icons";
import { useBrand } from "@/lib/brand-context";

type Props = {
  whatsappUrl?: string | null;
  facebookUrl?: string | null;
  instagramUrl?: string | null;
  youtubeUrl?: string | null;
  twitterUrl?: string | null;
  telegramUrl?: string | null;
  placement: "home" | "footer";
};

function trackClick(platform: string, placement: Props["placement"]) {
  try {
    if (localStorage.getItem("hf_cookie_consent") !== "accepted") return;
    window.gtag?.("event", "social_channel_click", { platform, placement });
  } catch { /* Storage may be unavailable; following the link still works. */ }
}

export default function SocialFollowCards({ whatsappUrl, facebookUrl, instagramUrl, youtubeUrl, twitterUrl, telegramUrl, placement }: Props) {
  const brand = useBrand();
  const channels = [
    { platform: "whatsapp", url: whatsappUrl, label: "WhatsApp", icon: siWhatsapp, surface: "bg-(--social-whatsapp-bg)", badge: "bg-(--social-whatsapp)", hover: "hover:border-(--social-whatsapp)" },
    { platform: "facebook", url: facebookUrl, label: "Facebook", icon: siFacebook, surface: "bg-(--social-facebook-bg)", badge: "bg-(--social-facebook)", hover: "hover:border-(--social-facebook)" },
    { platform: "instagram", url: instagramUrl, label: "Instagram", icon: siInstagram, surface: "bg-(--social-instagram-bg)", badge: "bg-(--social-instagram)", hover: "hover:border-(--social-instagram)" },
    { platform: "youtube", url: youtubeUrl, label: "YouTube", icon: siYoutube, surface: "bg-(--social-youtube-bg)", badge: "bg-(--social-youtube)", hover: "hover:border-(--social-youtube)" },
    { platform: "twitter", url: twitterUrl, label: "Twitter", icon: siX, surface: "bg-(--social-x-bg)", badge: "bg-(--social-x)", hover: "hover:border-(--social-x)" },
    { platform: "telegram", url: telegramUrl, label: "Telegram", icon: siTelegram, surface: "bg-(--social-telegram-bg)", badge: "bg-(--social-telegram)", hover: "hover:border-(--social-telegram)" },
  ].filter((channel) => channel.url);

  if (!channels.length) return null;
  return (
    <section aria-label={`${brand.name} sosyal kanallarını takip et`} data-social-follow={placement} className="py-6">
      <h2 className="type-h2 mb-4">Piyasadan haberdar ol</h2>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
        {channels.map(({ platform, url, label, icon, surface, badge, hover }) => (
          <a key={platform} href={url!} target="_blank" rel="noopener noreferrer nofollow"
            onClick={() => trackClick(platform, placement)}
            aria-label={`${label} hesabımızı aç`}
            className={`group flex min-h-16 min-w-0 items-center gap-2 rounded-sm border border-(--color-border-soft) px-2 text-label font-semibold text-(--color-foreground) transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--color-accent) ${surface} ${hover}`}>
            <span className={`flex size-9 shrink-0 items-center justify-center rounded-sm text-(--social-logo) ${badge}`}>
              <svg viewBox="0 0 24 24" fill="currentColor" className="size-5" aria-hidden="true"><path d={icon.path} /></svg>
            </span>
            <span className="min-w-0 truncate">{label}</span>
            <ArrowUpRight className="ml-auto size-4 shrink-0 text-(--color-muted)" aria-hidden="true" />
          </a>
        ))}
      </div>
    </section>
  );
}
